import { isEdge, isMobileUserAgent } from "../utils/device"

export const screenCaptureLifecycle = {
  endpoint() {
    return this.wizardModeValue ? this.wizardUrlValue : this.analyzeUrlValue
  },

  stop() {
    this.teardown()
    this.setStatus(this.t("stopped", "Stopped."), "neutral")
  },

  handleShareEnded() {
    if (this.stoppingStream || !this.running || this.completionReady()) return

    this.teardown()
    this.session = null
    this.validationWindow = null
    this.previousFrameDataUrl = null
    this.clearWizardResult({ preserveCaptures: false })
    this.markSubstepOpenPending()
    this.showInterruptionNotice()
    this.setStatus(
      this.t("interrupted", "Screen sharing stopped before verification finished. Restart from the verification page step."),
      "error"
    )
    this.bringUserBackToApplication()
  },

  teardown() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }

    const stream = this.stream
    this.stream = null
    this.stoppingStream = true
    try {
      stream?.getTracks().forEach((track) => track.stop())
    } finally {
      this.stoppingStream = false
    }

    if (this.hasVideoTarget) this.videoTarget.srcObject = null
    this.running = false
    this.applyShareButtonGate()
    this.markSubstepShareActive(false)
    this.renderCapturedState()
  },

  screenShareApiAvailable() {
    return (
      typeof window !== "undefined" &&
      window.isSecureContext &&
      typeof navigator !== "undefined" &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getDisplayMedia === "function"
    )
  },

  async requestDisplayMediaStream() {
    const attempts = [
      {
        video: { frameRate: { ideal: 2, max: 5 }, displaySurface: "browser" },
        preferCurrentTab: false,
        selfBrowserSurface: "exclude",
        surfaceSwitching: "exclude",
        monitorTypeSurfaces: "exclude",
        audio: false
      },
      {
        video: { frameRate: { ideal: 2, max: 5 }, displaySurface: "browser" },
        audio: false
      },
      {
        video: { frameRate: { ideal: 2 } },
        audio: false
      },
      {
        video: true,
        audio: false
      }
    ]

    let lastError = null
    for (const constraints of attempts) {
      try {
        return await navigator.mediaDevices.getDisplayMedia(constraints)
      } catch (error) {
        lastError = error
        if (error?.name === "NotAllowedError" || error?.name === "AbortError") throw error
      }
    }

    throw lastError || new Error("getDisplayMedia failed")
  },

  shareErrorMessage(error) {
    const name = error?.name || ""
    const mobileHint = isMobileUserAgent()
      ? this.t("share_errors.mobile_hint", " On phones and tablets, try a computer or use Apple Wallet.")
      : ""

    if (name === "NotAllowedError" || name === "AbortError") {
      const edgeHint = isEdge()
        ? this.t("share_errors.edge_picker", " In Edge, choose the verification tab when the picker appears.")
        : this.t("share_errors.tab_picker", " When prompted, select the verification tab opened in step 1.")
      return `${this.t("share_errors.denied", "Screen sharing was cancelled or permission was denied.")}${edgeHint}${mobileHint}`
    }

    if (name === "NotSupportedError" || name === "SecurityError") {
      if (!window.isSecureContext) {
        return this.t("share_errors.secure_context", "Screen sharing requires HTTPS. Use a secure connection or choose Apple Wallet instead.")
      }
      return `${this.t("share_errors.blocked", "Screen sharing is blocked in this browser.")}${mobileHint}`
    }

    if (name === "OverconstrainedError" || name === "TypeError") {
      const edgeHint = isEdge() ? this.t("share_errors.update_edge", " Try updating Edge.") : ""
      return `${this.t("share_errors.could_not_start", "Could not start screen sharing in this browser.")}${edgeHint}${mobileHint}`
    }

    return `${this.t("share_errors.start_failed", "Screen sharing could not start.")}${mobileHint}`
  },

  bringUserBackToApplication() {
    try {
      window.focus()
    } catch {
      // Browsers may reject programmatic focus.
    }
    requestAnimationFrame(() => {
      this.element.scrollIntoView({ behavior: "smooth", block: "center" })
    })
  }
}
